/**
 * Scadenze: obblighi da aggiornare, documenti alla fine della pubblicazione e
 * file da sostituire.
 *
 * CONVENTIONS §5 non prevede (ancora) un endpoint dedicato alle scadenze. Invece
 * di inventarne uno, il back-office **deriva** lo scadenzario dai due dati che
 * l'API espone gia': l'alberatura (`GET /api/v1/nodi`) e l'elenco delle
 * pubblicazioni (`GET /api/v1/pubblicazioni`). Il calcolo e' tutto qui, in un
 * modulo isolato, cosi' che il giorno in cui l'API esporra' le scadenze basti
 * sostituire il chiamante.
 *
 * Tre famiglie di scadenza, e **tre concetti diversi** che non vanno confusi:
 *
 * * **aggiornamento periodico** dell'obbligo — va rinnovato secondo la sua
 *   frequenza (annuale, semestrale, trimestrale, tempestiva). E' un dato
 *   dell'obbligo, e dove il Comune non l'ha dichiarato e' un'ipotesi;
 * * **fine della pubblicazione** di ciascun documento (art. 8 c. 3 d.lgs.
 *   33/2013) — ogni documento ha la propria durata: cinque anni, salvo che il
 *   documento o l'obbligo ne dichiarino un'altra. Trascorsa, il documento esce
 *   dalla navigazione corrente ma **resta accessibile**, non viene mai
 *   cancellato. Con la frequenza di aggiornamento non ha nulla a che fare;
 * * **scadenza del file** (`valido_fino_al`) — non normativa: la dichiara chi
 *   carica, ed e' il giorno entro cui il file va sostituito con uno aggiornato.
 *   Passata, il documento **resta pubblicato**; con la durata non ha nulla a
 *   che fare.
 *
 * Il Comune leggeva la seconda nella colonna della prima: «annuale, annuale…»
 * dove si aspettava le durate dei documenti. Per questo le righe di
 * archiviazione non hanno una cadenza, e portano invece il documento, la
 * decorrenza, la durata e l'ultimo giorno.
 *
 * Il calcolo e' volutamente prudente: quando una data manca la scadenza non
 * viene inventata, la voce e' semplicemente assente.
 */

import type {
  DataIt,
  Documento,
  IdNodo,
  Nodo,
  Pubblicazione,
  RegolaObbligo,
} from "../api/types";
import { dataDaTesto, giorniDaOggi, testoDaData } from "./formats";

/** Finestra di preavviso usata dal KPI «in scadenza» del cruscotto. */
export const GIORNI_PREAVVISO = 30;

/** Durata della pubblicazione in assenza di dichiarazioni, art. 8 c. 3 d.lgs. 33/2013. */
export const DURATA_PREDEFINITA_ANNI = 5;

/** Intervallo ammesso per una durata dichiarata: lo stesso dell'API. */
export const DURATA_MINIMA_ANNI = 1;
export const DURATA_MASSIMA_ANNI = 30;

/**
 * Tetto del menu delle durate **al caricamento**.
 *
 * Trenta voci erano un elenco da scorrere per una scelta che, caricando, va
 * quasi sempre da uno a cinque anni. Le durate piu' lunghe restano possibili
 * con «Fino a una data…», e dopo il caricamento con «Durata…»: il limite e' del
 * menu, non dell'API.
 */
export const DURATA_MASSIMA_AL_CARICAMENTO = 5;

/** Gli anni offerti dal menu delle durate, da {@link DURATA_MINIMA_ANNI} a `massimo`. */
export function anniDelMenu(massimo: number = DURATA_MASSIMA_ANNI): number[] {
  const tetto = Math.min(massimo, DURATA_MASSIMA_ANNI);
  const anni: number[] = [];
  for (let valore = DURATA_MINIMA_ANNI; valore <= tetto; valore += 1) anni.push(valore);
  return anni;
}

export type Cadenza = "tempestivo" | "trimestrale" | "semestrale" | "annuale";

/** Giorni di validita' associati a ciascuna cadenza. */
const GIORNI_CADENZA: Record<Cadenza, number> = {
  tempestivo: 90,
  trimestrale: 92,
  semestrale: 183,
  annuale: 366,
};

export const ETICHETTE_CADENZA: Record<Cadenza, string> = {
  tempestivo: "Tempestivo",
  trimestrale: "Trimestrale",
  semestrale: "Semestrale",
  annuale: "Annuale",
};

/**
 * Cadenze di aggiornamento per i nodi in cui il d.lgs. 33/2013 le fissa in modo
 * esplicito. Per tutti gli altri nodi vale la cadenza annuale, che e' il
 * riferimento prudenziale.
 */
const CADENZE: Record<IdNodo, Cadenza> = {
  disposizioni_generali: "annuale",
  organizzazione: "tempestivo",
  consulenti_collaboratori: "tempestivo",
  personale: "annuale",
  bandi_concorso: "tempestivo",
  performance: "annuale",
  enti_controllati: "annuale",
  attivita_procedimenti: "tempestivo",
  provvedimenti: "semestrale",
  controlli_imprese: "annuale",
  bandi_gara_contratti: "tempestivo",
  sovvenzioni_contributi_sussidi_vantaggi_economici: "tempestivo",
  bilanci: "annuale",
  beni_immobili_gestione_patrimonio: "annuale",
  controlli_rilievi_amministrazione: "annuale",
  servizi_erogati: "annuale",
  pagamenti_amministrazione: "trimestrale",
  opere_pubbliche: "annuale",
  pianificazione_governo_territorio: "tempestivo",
  informazioni_ambientali: "tempestivo",
  interventi_straordinari_emergenza: "tempestivo",
  // La sezione in piu' dell'albero adottato rispetto all'elenco canonico ANAC.
  // Mancava, e la sua assenza non si vedeva: tutto il ramo ricadeva sul
  // fallback annuale senza che nulla lo dichiarasse. Resta «annuale» — nessuna
  // fonte ANAC fissa una periodicita' per questi obblighi — ma ora e' una
  // scelta scritta, non un silenzio.
  strutture_sanitarie_private_accreditate: "annuale",
  altri_contenuti: "annuale",
};

type NodoRisalibile = Pick<Nodo, "id_nodo" | "id_padre">;

/**
 * Cadenza di un nodo, risalendo la catena degli antenati.
 *
 * Le cadenze sono indicizzate sui nodi di primo livello. Con l'alberatura a tre
 * livelli guardare al solo padre non basta piu': per un obbligo il padre e' una
 * sottosezione, che non compare in `CADENZE`, e la scadenza ricadrebbe in
 * silenzio su «annuale» anche dove la norma impone il tempestivo. Passando
 * `perIdNodo` la risalita arriva fino alla sezione.
 */
export function cadenzaDelNodo(
  nodo: NodoRisalibile,
  perIdNodo?: ReadonlyMap<string, NodoRisalibile>,
): Cadenza {
  return cadenzaConFonte(nodo, perIdNodo).cadenza;
}

/** Da dove viene la cadenza che si sta applicando a un nodo. */
export type FonteCadenza = "dichiarata" | "sezione" | "predefinita";

export interface CadenzaRisolta {
  cadenza: Cadenza;
  fonte: FonteCadenza;
  /** Nodo da cui la cadenza e' stata ereditata, quando `fonte` e' `sezione`. */
  id_nodo_fonte?: IdNodo;
}

/**
 * La cadenza di un nodo **con la sua provenienza**.
 *
 * La provenienza non e' un di piu' documentale: e' la differenza fra un dato e
 * un'ipotesi, e l'interfaccia deve poterla dire. Dei 121 obblighi di terzo
 * livello nessuno ha una cadenza propria — la ereditano tutti dalla sezione — e
 * sotto «Bilanci: annuale» cadono insieme il «Bilancio preventivo», che ogni
 * anno sostituisce il precedente, e i «Documenti e allegati del bilancio», che
 * si accumulano. Presentare quel valore come un fatto sarebbe una bugia
 * tipografica.
 *
 * `predefinita` e' il caso in cui nessun antenato compare in `CADENZE`: prima
 * era indistinguibile da un'eredita' legittima, e per la 23ª sezione lo e'
 * stato per mesi.
 *
 * `cadenzaDichiarata` ha la precedenza su tutto: e' il Comune che corregge
 * l'ipotesi, e la sua parola vale piu' della nostra tabella.
 */
export function cadenzaConFonte(
  nodo: NodoRisalibile,
  perIdNodo?: ReadonlyMap<string, NodoRisalibile>,
  cadenzaDichiarata?: Cadenza,
): CadenzaRisolta {
  if (cadenzaDichiarata) return { cadenza: cadenzaDichiarata, fonte: "dichiarata" };

  let corrente: NodoRisalibile | undefined = nodo;
  const visti = new Set<string>();
  while (corrente && !visti.has(corrente.id_nodo)) {
    const cadenza = CADENZE[corrente.id_nodo];
    if (cadenza) {
      return { cadenza, fonte: "sezione", id_nodo_fonte: corrente.id_nodo };
    }
    visti.add(corrente.id_nodo);
    if (!corrente.id_padre) break;
    corrente = perIdNodo?.get(corrente.id_padre) ?? {
      id_nodo: corrente.id_padre,
      id_padre: null,
    };
  }
  return { cadenza: "annuale", fonte: "predefinita" };
}

/**
 * Da dove viene una cadenza, in una riga leggibile.
 *
 * Una copia sola: la mostrano le due tabelle di «Scadenze e allerte» e la
 * modale che la dichiara, e tre copie della stessa frase divergono alla prima riformulazione
 * — proprio mentre il senso di mostrarla e' che le pagine dicano la stessa cosa.
 */
export function testoProvenienzaCadenza(fonte: FonteCadenza, idNodoFonte?: IdNodo): string {
  if (fonte === "dichiarata") return "dichiarata dal Comune";
  if (fonte === "sezione") return `ipotesi: ereditata da ${idNodoFonte ?? "la sezione"}`;
  return "ipotesi: nessuna cadenza a catalogo, si applica l'annuale prudenziale";
}

/** Giorni di validita' di una cadenza. */
export function giorniDiCadenza(cadenza: Cadenza): number {
  return GIORNI_CADENZA[cadenza];
}

/**
 * Primo giorno fuori pubblicazione di un contenuto **senza documenti**.
 *
 * **La formula e' quella dell'API** (`data_scadenza_quinquennale`), che e'
 * anche quella che il worker di archiviazione applica: il 1º gennaio dopo la
 * durata, contata dall'anno successivo alla prima pubblicazione. Qui c'era
 * invece `5 * 365 + giorniDaOggi(prima)`, che su un documento del 30/03/2021
 * dichiarava scaduto il 29/03/2026 anziche' il 01/01/2027 — dieci mesi di
 * anticipo, e nel verso peggiore.
 *
 * Resta come **ripiego**: la data autorevole arriva nella risposta
 * (`Pubblicazione.data_archiviazione_prevista`) e va preferita quando c'e'.
 */
export function dataArchiviazionePrevista(
  prima: DataIt,
  anni: number = DURATA_PREDEFINITA_ANNI,
): DataIt | null {
  const data = dataDaTesto(prima.trim().slice(0, 10));
  if (!data) return null;
  return testoDaData(new Date(data.getFullYear() + 1 + anni, 0, 1));
}

/**
 * Ultimo giorno di pubblicazione di un documento, compreso.
 *
 * E' lo **specchio della formula dell'API** (`fine_pubblicazione` in
 * `services/pubblicazione.py`), e l'unico del back-office. In ordine:
 *
 * 1. `finoAl`, se il documento ha una data di fine;
 * 2. la decorrenza piu' la durata del documento, se ne ha una;
 * 3. la decorrenza piu' la durata dell'obbligo, cinque anni in assenza.
 *
 * N anni da un documento del 30/03/2021 finiscono il 31/12/(2021 + N): l'art. 8
 * li fa decorrere dal 1º gennaio successivo. Le verifiche
 * (`verifiche/durata.verifica.ts`) ripetono i casi dei test dell'API.
 *
 * `null` senza decorrenza e senza data di fine: un documento mai pubblicato
 * non scade.
 */
export function finePubblicazione(
  decorrenza: DataIt | null | undefined,
  durata: { durataAnni?: number | null; finoAl?: DataIt | null; durataObbligo?: number | null },
): DataIt | null {
  if (durata.finoAl) return durata.finoAl;
  if (!decorrenza) return null;
  const data = dataDaTesto(decorrenza.trim().slice(0, 10));
  if (!data) return null;
  const anni = durata.durataAnni ?? durata.durataObbligo ?? DURATA_PREDEFINITA_ANNI;
  return testoDaData(new Date(data.getFullYear() + anni, 11, 31));
}

/** Da dove viene la durata di un documento. */
export type FonteDurata = "documento" | "obbligo" | "legge";

export interface DurataRisolta {
  /** Anni di durata; nullo quando vale una data di fine. */
  anni: number | null;
  /** Ultimo giorno dichiarato, quando la fine e' una data e non una durata. */
  fino_al: DataIt | null;
  fonte: FonteDurata;
}

/**
 * La durata che si applica a un documento, con la sua provenienza.
 *
 * Come per la cadenza, la provenienza e' cio' che distingue un dato da un
 * valore di legge: «5 anni» puo' essere la scelta del Comune per quel
 * documento o semplicemente l'art. 8, e chi legge lo scadenzario deve saperlo.
 */
export function durataRisolta(
  documento: Pick<Documento, "durata_anni" | "fino_al">,
  regola?: Pick<RegolaObbligo, "durata_anni"> | null,
): DurataRisolta {
  if (documento.fino_al) return { anni: null, fino_al: documento.fino_al, fonte: "documento" };
  if (documento.durata_anni) {
    return { anni: documento.durata_anni, fino_al: null, fonte: "documento" };
  }
  if (regola?.durata_anni) return { anni: regola.durata_anni, fino_al: null, fonte: "obbligo" };
  return { anni: DURATA_PREDEFINITA_ANNI, fino_al: null, fonte: "legge" };
}

/** «1 anno», «3 anni». */
export function anniInTesto(anni: number): string {
  return `${anni} ${anni === 1 ? "anno" : "anni"}`;
}

/** La durata in una riga leggibile: «fino al 30/06/2035», «3 anni (regola dell'obbligo)». */
export function testoDurata(durata: DurataRisolta): string {
  if (durata.fino_al) return `fino al ${durata.fino_al}`;
  const anni = anniInTesto(durata.anni ?? DURATA_PREDEFINITA_ANNI);
  if (durata.fonte === "documento") return `${anni} (scelta per il documento)`;
  if (durata.fonte === "obbligo") return `${anni} (regola dell'obbligo)`;
  return `${anni} (art. 8 c. 3)`;
}

/**
 * Quanti documenti di un obbligo, con una durata dell'obbligo diversa, sarebbero
 * gia' oltre la fine.
 *
 * E' l'anteprima di cio' che la dichiarazione fara': cambiata la durata, l'API
 * archivia subito i documenti che risultano oltre la fine. Si contano come li
 * conta l'API — tutti i documenti correnti, anche quelli con una durata propria
 * gia' trascorsa — e con la stessa formula; non con `fine_pubblicazione`, che
 * e' calcolata con la regola di oggi. Un sostituto in bozza conta: sta al posto
 * di un documento online (non si archiviera', e la risposta lo dira').
 */
export function documentiOltreLaFineCon(
  pubblicazioni: readonly Pubblicazione[],
  idNodo: IdNodo,
  durataObbligo: number | null,
  oggi: DataIt,
): number {
  const riferimento = dataDaTesto(oggi);
  if (!riferimento) return 0;
  let quanti = 0;
  for (const pubblicazione of pubblicazioni) {
    if (pubblicazione.nodo_id !== idNodo || pubblicazione.stato !== "pubblicato") continue;
    for (const documento of pubblicazione.documenti ?? []) {
      if (documento.archiviato_il) continue;
      if (!documento.url && !documento.pubblicato_dal) continue; // mai pubblicato
      const fine = finePubblicazione(documento.pubblicato_dal, {
        durataAnni: documento.durata_anni,
        finoAl: documento.fino_al,
        durataObbligo,
      });
      const data = fine ? dataDaTesto(fine) : null;
      if (data && data < riferimento) quanti += 1;
    }
  }
  return quanti;
}

/**
 * Che cosa ha comportato subito una regola appena dichiarata.
 *
 * Cambiata la durata, l'API archivia i documenti che porta oltre la fine e
 * dice quali non ha potuto archiviare (una correzione in bozza); a durata
 * invariata conta soltanto quelli gia' oltre la fine, che il worker non ha
 * ancora raggiunto. Chi ha appena salvato deve saperlo ora, non scoprirlo fra
 * le righe dello scadenzario. `null` quando non c'e' nulla da dire.
 */
export function esitoDurataDichiarata(
  registrata: Pick<
    RegolaObbligo,
    "documenti_archiviati" | "archiviazioni_non_riuscite" | "documenti_oltre_la_fine"
  >,
): string | null {
  const frasi: string[] = [];
  const archiviati = registrata.documenti_archiviati ?? 0;
  if (archiviati > 0) {
    frasi.push(
      archiviati === 1
        ? "1 documento era oltre la nuova fine ed e' uscito subito dalla pagina: resta accessibile nell'archivio."
        : `${archiviati} documenti erano oltre la nuova fine e sono usciti subito dalla pagina: restano accessibili nell'archivio.`,
    );
  }
  const mancati = registrata.archiviazioni_non_riuscite ?? [];
  if (mancati.length > 0) {
    frasi.push(`Restano online, oltre la fine: ${mancati.join("; ")}.`);
  } else if (registrata.documenti_oltre_la_fine) {
    const quanti = registrata.documenti_oltre_la_fine;
    const chi =
      quanti === 1
        ? "1 documento dell'obbligo ha gia' superato"
        : `${quanti} documenti dell'obbligo hanno gia' superato`;
    frasi.push(
      `${chi} la fine della pubblicazione: compaiono fra i «Documenti in scadenza di pubblicazione», da archiviare con «Archivia ora».`,
    );
  }
  return frasi.length > 0 ? frasi.join(" ") : null;
}

/**
 * La prima voce del menu della durata: che cosa succede se non si sceglie nulla.
 *
 * `regoleNote` falso — le regole degli obblighi sono ancora in arrivo, o non
 * sono arrivate — e non si nomina alcun numero: «5 anni» detto di un obbligo
 * che ne ha 3 sarebbe un'informazione falsa proprio sulla voce che quasi tutti
 * lasciano com'e'. La regola la applica comunque l'API, che la conosce.
 */
export function etichettaDurataPredefinita(
  durataObbligo: number | null | undefined,
  regoleNote = true,
): string {
  if (!regoleNote) return "Come da regola dell'obbligo";
  return durataObbligo
    ? `Come da regola dell'obbligo: ${anniInTesto(durataObbligo)}`
    : `Come da regola generale: ${anniInTesto(DURATA_PREDEFINITA_ANNI)} (art. 8 c. 3)`;
}

/**
 * L'ultimo giorno di un documento: quello dell'API se c'e', altrimenti il calcolo locale.
 */
export function fineDelDocumento(
  documento: Pick<Documento, "fine_pubblicazione" | "pubblicato_dal" | "durata_anni" | "fino_al">,
  regola?: Pick<RegolaObbligo, "durata_anni"> | null,
): DataIt | null {
  return (
    documento.fine_pubblicazione ??
    finePubblicazione(documento.pubblicato_dal, {
      durataAnni: documento.durata_anni,
      finoAl: documento.fino_al,
      durataObbligo: regola?.durata_anni,
    })
  );
}

/**
 * La durata come la sceglie un modulo, in una stringa sola.
 *
 * `""` vale «come la regola dell'obbligo» — ed e' il valore di partenza, perche'
 * la durata di un documento e' quasi sempre quella dell'obbligo —, `"anni:N"`
 * una durata propria in anni, `"data"` una data di fine da scrivere a parte.
 * Una stringa e non un oggetto perche' e' il valore di un `<select>`.
 */
export type SceltaDurata = "" | "data" | `anni:${number}`;

/** La scelta che corrisponde alla durata gia' dichiarata su un documento. */
export function sceltaDelDocumento(
  documento: Pick<Documento, "durata_anni" | "fino_al">,
): { scelta: SceltaDurata; finoAl: string } {
  if (documento.fino_al) return { scelta: "data", finoAl: documento.fino_al };
  if (documento.durata_anni) return { scelta: `anni:${documento.durata_anni}`, finoAl: "" };
  return { scelta: "", finoAl: "" };
}

/**
 * Traduce la scelta del modulo nei due campi dell'API, o spiega perche' non si puo'.
 *
 * Le stesse regole dell'API (`valida_durata`), ripetute qui per dirle **prima**
 * dell'invio: una data illeggibile o gia' passata — che archivierebbe il
 * documento alla prima esecuzione del worker — e una durata fuori misura.
 */
export function leggiDurata(
  scelta: SceltaDurata,
  finoAl: string,
): { durata_anni: number | null; fino_al: DataIt | null; errore: string | null } {
  if (scelta === "") return { durata_anni: null, fino_al: null, errore: null };
  if (scelta === "data") {
    const testo = finoAl.trim();
    if (!dataDaTesto(testo)) {
      return {
        durata_anni: null,
        fino_al: null,
        errore: "Scrivere l'ultimo giorno di pubblicazione come gg/mm/aaaa.",
      };
    }
    const residui = giorniDaOggi(testo);
    if (residui !== null && residui < 0) {
      return {
        durata_anni: null,
        fino_al: null,
        errore:
          "La data e' gia' passata: il documento uscirebbe subito dalla pubblicazione. Per ritirarlo si usa «Archivia».",
      };
    }
    return { durata_anni: null, fino_al: testo, errore: null };
  }
  const anni = Number(scelta.slice("anni:".length));
  if (!Number.isInteger(anni) || anni < DURATA_MINIMA_ANNI || anni > DURATA_MASSIMA_ANNI) {
    return {
      durata_anni: null,
      fino_al: null,
      errore: `La durata va da ${DURATA_MINIMA_ANNI} a ${DURATA_MASSIMA_ANNI} anni.`,
    };
  }
  return { durata_anni: anni, fino_al: null, errore: null };
}

/**
 * La scadenza del file che conta, o `null` se non ce n'e' una da sorvegliare.
 *
 * E' **una regola sola** per le allerte («File da sostituire», a scadenza
 * passata) e per «In arrivo» (a scadenza futura): la pagina divide i file fra
 * le due schede con lo stesso conto dei giorni, e due copie della regola
 * finirebbero, al primo ritocco, per mostrare un file in entrambe o in nessuna.
 *
 * * un file archiviato e' uscito: nulla da sostituire;
 * * un file online porta la propria;
 * * un sostituto in bozza (senza indirizzo, con la decorrenza ereditata) porta
 *   quella del file online che rimpiazzera': finche' non e' pubblicato, online
 *   c'e' ancora il file scaduto. `inCorrezione` lo dice;
 * * un file nuovo mai pubblicato non e' online: la sua scadenza conta da quando
 *   lo sara'.
 */
export function scadenzaDelFile(
  documento: Pick<
    Documento,
    "archiviato_il" | "url" | "pubblicato_dal" | "valido_fino_al" | "valido_fino_al_sostituito"
  >,
): { data: DataIt; inCorrezione: boolean } | null {
  if (documento.archiviato_il) return null;
  if (documento.url) {
    return documento.valido_fino_al ? { data: documento.valido_fino_al, inCorrezione: false } : null;
  }
  if (documento.pubblicato_dal && documento.valido_fino_al_sostituito) {
    return { data: documento.valido_fino_al_sostituito, inCorrezione: true };
  }
  return null;
}

/**
 * Legge la scadenza del file scritta in un modulo, o spiega perche' non va.
 *
 * Vuota vale «nessuna scadenza». Una data gia' passata si rifiuta, come fa
 * l'API: il file nascerebbe gia' da sostituire.
 */
export function leggiScadenzaFile(testo: string): { valore: DataIt | null; errore: string | null } {
  const pulito = testo.trim();
  if (!pulito) return { valore: null, errore: null };
  if (!dataDaTesto(pulito)) {
    return { valore: null, errore: "Scrivere la scadenza come gg/mm/aaaa." };
  }
  const residui = giorniDaOggi(pulito);
  if (residui !== null && residui < 0) {
    return {
      valore: null,
      errore: "La data e' gia' passata: il file risulterebbe subito da sostituire.",
    };
  }
  return { valore: pulito, errore: null };
}

/** Il giorno prima di una data: da «primo giorno fuori» a «ultimo giorno dentro». */
export function giornoPrima(data: DataIt): DataIt | null {
  const valore = dataDaTesto(data.trim().slice(0, 10));
  if (!valore) return null;
  valore.setDate(valore.getDate() - 1);
  return testoDaData(valore);
}

/** Indice `id_nodo` → nodo, per risalire la catena degli antenati. */
export function indicePerIdNodo(nodi: readonly Nodo[]): ReadonlyMap<string, Nodo> {
  return new Map(appiattisciAlberatura(nodi).map((nodo) => [nodo.id_nodo, nodo]));
}

/**
 * Le quattro famiglie di riga dello scadenzario.
 *
 * `scoperto` e' stato separato da `aggiornamento` dopo la terza tornata di
 * verifica, ed e' una **decisione di prodotto**, non un dettaglio di tipi.
 *
 * Un obbligo mai valorizzato non ha una scadenza: non c'e' una data da cui
 * contare i giorni della cadenza, perche' non c'e' mai stata una pubblicazione.
 * Iscriverlo fra gli adempimenti con zero giorni residui lo faceva comparire
 * come «scade oggi» e — cosa peggiore — lo faceva contare dal KPI «In scadenza»
 * del cruscotto, che su un ente appena avviato leggeva 121 come se 121 termini
 * stessero per spirare. Erano i 121 obblighi vuoti, gia' contati dal KPI
 * accanto, «Nodi senza contenuti».
 *
 * La riga resta prodotta — l'informazione serve, ed e' anche piu' larga di
 * quella del KPI accanto, che guarda le sole foglie — ma con un tipo suo, che
 * {@link scadenzeImminenti} e la tabella degli adempimenti escludono. I due
 * numeri del cruscotto tornano cosi' a misurare due cose diverse.
 */
export type TipoScadenza = "aggiornamento" | "archiviazione" | "validita" | "scoperto";

/** Etichette dei tipi, come compaiono nella colonna «Che cosa scade» di «Scadenze e allerte». */
export const ETICHETTE_TIPO_SCADENZA: Record<TipoScadenza, string> = {
  aggiornamento: "Aggiornamento",
  archiviazione: "Fine pubblicazione",
  validita: "Scadenza del file",
  scoperto: "Scoperto",
};

/** Cio' che tutte le righe hanno in comune. */
interface ScadenzaComune {
  id_nodo: IdNodo;
  denominazione_nodo: string;
  /** Pubblicazione interessata; assente per un nodo che non ha mai pubblicato. */
  pubblicazione?: Pick<Pubblicazione, "id" | "titolo" | "anno_riferimento" | "stato">;
  /** Giorni residui: negativo se la scadenza e' gia' passata. */
  giorni_residui: number;
  /** Descrizione dell'adempimento, in italiano. */
  descrizione: string;
}

/** Una riga che riguarda l'obbligo e il suo aggiornamento: ha una cadenza. */
export interface ScadenzaAggiornamento extends ScadenzaComune {
  tipo: "aggiornamento" | "scoperto";
  cadenza: Cadenza;
  /**
   * Da dove viene la cadenza: dichiarata dal Comune, ereditata dalla sezione,
   * o il ripiego annuale. E' la differenza fra un termine e un'ipotesi di
   * termine, e lo scadenzario deve poterla dire come la dicono le allerte.
   */
  fonte_cadenza: FonteCadenza;
  /** Nodo da cui la cadenza e' ereditata, quando `fonte_cadenza` e' `sezione`. */
  id_nodo_cadenza?: IdNodo;
}

/**
 * Una riga che riguarda la fine della pubblicazione: **non** ha una cadenza.
 *
 * Una per documento, perche' ogni documento ha la propria durata. Le
 * pubblicazioni senza documenti — una pagina fatta di soli dati — hanno una
 * riga propria, senza `documento`.
 */
export interface ScadenzaArchiviazione extends ScadenzaComune {
  tipo: "archiviazione";
  /**
   * `url` nullo vuol dire che la riga e' un file corretto in attesa di
   * pubblicazione: ha la scadenza della famiglia, ma non si archivia — non e'
   * ancora online.
   */
  documento?: Pick<Documento, "id" | "titolo" | "url">;
  /** Decorrenza: prima pubblicazione del documento o della sua famiglia. */
  pubblicato_dal: DataIt | null;
  /** La durata che si applica; nulla per una pubblicazione senza documenti. */
  durata: DurataRisolta | null;
  /** Ultimo giorno in pubblicazione corrente, compreso. */
  fine: DataIt;
}

/**
 * Una riga che riguarda la scadenza dichiarata sul file: da quel giorno il file
 * va sostituito, ma resta pubblicato. Non ha ne' cadenza ne' durata.
 */
export interface ScadenzaValidita extends ScadenzaComune {
  tipo: "validita";
  documento: Pick<Documento, "id" | "titolo" | "url">;
  /** Ultimo giorno di validita' dichiarato sul file. */
  valido_fino_al: DataIt;
  /** Il sostituto e' gia' caricato, in bozza: resta da pubblicarlo. */
  in_correzione: boolean;
}

/** Una riga dello scadenzario. */
export type Scadenza = ScadenzaAggiornamento | ScadenzaArchiviazione | ScadenzaValidita;

/** La cadenza di una riga d'aggiornamento, con la sua provenienza. */
export function cadenzaRisoltaDi(scadenza: ScadenzaAggiornamento): CadenzaRisolta {
  return {
    cadenza: scadenza.cadenza,
    fonte: scadenza.fonte_cadenza,
    id_nodo_fonte: scadenza.id_nodo_cadenza,
  };
}

/** Appiattisce l'alberatura annidata in un elenco di nodi. */
export function appiattisciAlberatura(nodi: readonly Nodo[]): Nodo[] {
  const elenco: Nodo[] = [];
  for (const nodo of nodi) {
    elenco.push(nodo);
    if (nodo.figli) elenco.push(...appiattisciAlberatura(nodo.figli));
  }
  return elenco;
}

/** Nodi su cui il contenuto si pubblica davvero: le foglie dell'alberatura. */
export function nodiFoglia(nodi: readonly Nodo[]): Nodo[] {
  return appiattisciAlberatura(nodi).filter((nodo) => !nodo.figli || nodo.figli.length === 0);
}

/** Pubblicazione piu' recente per ciascun nodo, fra quelle pubblicate. */
function ultimaPubblicazionePerNodo(
  pubblicazioni: readonly Pubblicazione[],
): Map<IdNodo, Pubblicazione> {
  const ultime = new Map<IdNodo, Pubblicazione>();
  for (const pubblicazione of pubblicazioni) {
    if (pubblicazione.stato !== "pubblicato" || !pubblicazione.nodo_id) continue;
    const precedente = ultime.get(pubblicazione.nodo_id);
    if (!precedente || pubblicazione.anno_riferimento > precedente.anno_riferimento) {
      ultime.set(pubblicazione.nodo_id, pubblicazione);
    }
  }
  return ultime;
}

/**
 * Costruisce lo scadenzario ordinato per urgenza (le scadenze gia' superate per
 * prime).
 *
 * `regole` sono le nature dichiarate dal Comune (`GET /valutazioni/regole`):
 * dove una regola porta una cadenza, vale quella e non l'ipotesi della
 * sezione. Le allerte le applicavano gia'; lo scadenzario no, e una cadenza
 * dichiarata «trimestrale» continuava a produrre qui un termine annuale — due
 * pagine, due scadenze per lo stesso obbligo.
 */
export function calcolaScadenze(
  nodi: readonly Nodo[],
  pubblicazioni: readonly Pubblicazione[],
  regole: readonly RegolaObbligo[] = [],
): Scadenza[] {
  const scadenze: Scadenza[] = [];
  const ultime = ultimaPubblicazionePerNodo(pubblicazioni);
  // L'indice serve a risalire dagli obblighi alla sezione che ne fissa la
  // cadenza: senza, ogni nodo di terzo livello ricadrebbe su «annuale».
  const perIdNodo = indicePerIdNodo(nodi);
  const dichiarate = cadenzeDichiarate(regole);
  const nature = regolePerNodo(regole);
  const risolvi = (nodo: NodoRisalibile) =>
    cadenzaConFonte(nodo, perIdNodo, dichiarate.get(nodo.id_nodo));

  // Si monitorano le foglie — i nodi su cui si pubblica — piu' i nodi che una
  // pubblicazione cita davvero anche se foglie non sono. Il secondo insieme non
  // e' teorico: le sottosezioni ERANO le foglie prima del terzo livello, e una
  // pubblicazione rimasta agganciata a una di loro sparirebbe dal monitoraggio
  // dell'aggiornamento periodico senza che nulla lo segnali.
  const daMonitorare: Nodo[] = [...nodiFoglia(nodi)];
  const giaMonitorati = new Set(daMonitorare.map((nodo) => nodo.id_nodo));
  for (const idNodo of ultime.keys()) {
    if (giaMonitorati.has(idNodo)) continue;
    const nodo = perIdNodo.get(idNodo);
    if (!nodo) continue;
    giaMonitorati.add(idNodo);
    daMonitorare.push(nodo);
  }

  for (const nodo of daMonitorare) {
    // Un nodo dichiarato non applicabile espone la dicitura ANAC e non scade.
    if (!nodo.applicabile) continue;

    const risolta = risolvi(nodo);
    const cadenza = risolta.cadenza;
    const provenienza = {
      fonte_cadenza: risolta.fonte,
      id_nodo_cadenza: risolta.id_nodo_fonte,
    };
    const ultima = ultime.get(nodo.id_nodo);

    if (!ultima) {
      // Nodo applicabile e mai valorizzato. Non e' una scadenza: e' una
      // mancanza gia' in atto, e non ha una data da cui contare. Zero giorni
      // residui e' un segnaposto per l'ordinamento, non un termine.
      scadenze.push({
        tipo: "scoperto",
        id_nodo: nodo.id_nodo,
        denominazione_nodo: nodo.denominazione,
        cadenza,
        ...provenienza,
        giorni_residui: 0,
        descrizione: "Nessun contenuto pubblicato: l'obbligo risulta scoperto.",
      });
      continue;
    }

    // Un obbligo che il Comune ha dichiarato «non ricorrente» si valorizza
    // quando l'evento accade: non ha una cadenza da rispettare, quindi nemmeno
    // un termine. E' la stessa regola delle allerte, che non aprono
    // «aggiornamento scaduto» su questi obblighi: senza, lo scadenzario e il
    // cruscotto li davano in ritardo mentre le allerte tacevano.
    if (nature.get(nodo.id_nodo)?.natura === "non_ricorrente") continue;

    const aggiornatoIl = nodo.data_ultimo_aggiornamento ?? ultima.data_ultimo_aggiornamento;
    if (aggiornatoIl) {
      const giorniTrascorsiNegativi = giorniDaOggi(aggiornatoIl);
      if (giorniTrascorsiNegativi !== null) {
        const residui = GIORNI_CADENZA[cadenza] + giorniTrascorsiNegativi;
        scadenze.push({
          tipo: "aggiornamento",
          id_nodo: nodo.id_nodo,
          denominazione_nodo: nodo.denominazione,
          pubblicazione: ultima,
          cadenza,
          ...provenienza,
          giorni_residui: residui,
          descrizione: `Aggiornamento a cadenza ${ETICHETTE_CADENZA[cadenza].toLowerCase()}; ultimo aggiornamento ${aggiornatoIl}.`,
        });
      }
    }
  }

  // ------------------------------------------------ fine pubblicazione -- //
  //
  // Una riga per documento, senza orizzonte: lo decide la pagina. Qui c'era un
  // taglio a 180 giorni che, con la scelta «12 mesi», nascondeva proprio le
  // scadenze che si erano chieste, mentre le Allerte le mostravano.
  for (const pubblicazione of pubblicazioni) {
    if (pubblicazione.stato !== "pubblicato") continue;
    const idNodo = pubblicazione.nodo_id ?? "";
    const nodoPubblicato = idNodo ? perIdNodo.get(idNodo) : undefined;
    const comune = {
      id_nodo: idNodo,
      // Dall'indice arriva la denominazione ANAC, al posto dell'identificativo
      // in snake_case.
      denominazione_nodo: nodoPubblicato?.denominazione ?? (idNodo || "—"),
      pubblicazione,
    };
    const regola = nature.get(idNodo);
    const documenti = pubblicazione.documenti ?? [];

    if (documenti.length === 0) {
      const prima = pubblicazione.versioni?.[0]?.data_prima_pubblicazione;
      if (!prima) continue;
      // La data autorevole e' quella che l'API calcola e che il worker
      // applica; il calcolo locale resta per un'API che non la porti.
      const primoFuori =
        pubblicazione.data_archiviazione_prevista ??
        dataArchiviazionePrevista(prima, regola?.durata_anni ?? DURATA_PREDEFINITA_ANNI);
      const fine = primoFuori ? giornoPrima(primoFuori) : null;
      const residui = fine ? giorniDaOggi(fine) : null;
      if (!fine || residui === null) continue;
      scadenze.push({
        tipo: "archiviazione",
        ...comune,
        pubblicato_dal: prima,
        durata: null,
        fine,
        giorni_residui: residui,
        descrizione: `Contenuto senza documenti pubblicato dal ${prima}: resta in pubblicazione fino al ${fine}, poi passa in archivio e resta accessibile.`,
      });
      continue;
    }

    for (const documento of documenti) {
      const validita = scadenzaDelFile(documento);
      const residuiValidita = validita ? giorniDaOggi(validita.data) : null;
      if (validita && residuiValidita !== null) {
        scadenze.push({
          tipo: "validita",
          ...comune,
          documento: { id: documento.id, titolo: documento.titolo, url: documento.url },
          valido_fino_al: validita.data,
          in_correzione: validita.inCorrezione,
          giorni_residui: residuiValidita,
          descrizione: validita.inCorrezione
            ? `«${documento.titolo}»: il sostituto e' in bozza; online resta il file da sostituire entro il ${validita.data}.`
            : `«${documento.titolo}» va sostituito con una versione aggiornata entro il ${validita.data}; resta pubblicato.`,
        });
      }
      // Un documento archiviato e' gia' uscito. Uno mai pubblicato — senza
      // decorrenza: ne' online ne' revisione di qualcosa che lo e' — non ha
      // ancora una pubblicazione da far finire, anche se porta gia' una data.
      if (documento.archiviato_il || !documento.pubblicato_dal) continue;
      const fine = fineDelDocumento(documento, regola);
      if (!fine) continue;
      const residui = giorniDaOggi(fine);
      if (residui === null) continue;
      const durata = durataRisolta(documento, regola);
      scadenze.push({
        tipo: "archiviazione",
        ...comune,
        documento: { id: documento.id, titolo: documento.titolo, url: documento.url },
        pubblicato_dal: documento.pubblicato_dal ?? null,
        durata,
        fine,
        giorni_residui: residui,
        descrizione:
          `«${documento.titolo}»: pubblicato dal ${documento.pubblicato_dal ?? "—"}, ` +
          `${testoDurata(durata)}. Dopo il ${fine} esce dalla pubblicazione corrente e resta accessibile.`,
      });
    }
  }

  return scadenze.sort((prima, seconda) => prima.giorni_residui - seconda.giorni_residui);
}

/**
 * Una regola per obbligo.
 *
 * Se per lo stesso nodo ne arrivassero due — l'unicita' la impone il database,
 * non deve accadere — vale la **prima**, come in `lib/allerte.ts`: e' l'ordine
 * in cui l'API le serve. Una `Map` costruita per assegnazione terrebbe
 * l'ultima, e la modale mostrerebbe una dichiarazione mentre i termini ne usano
 * un'altra.
 */
export function regolePerNodo(
  regole: readonly RegolaObbligo[],
): ReadonlyMap<IdNodo, RegolaObbligo> {
  const perNodo = new Map<IdNodo, RegolaObbligo>();
  for (const regola of regole) {
    if (!perNodo.has(regola.id_nodo)) perNodo.set(regola.id_nodo, regola);
  }
  return perNodo;
}

/**
 * Cadenza dichiarata per ciascun obbligo, dalle regole che ne portano una.
 *
 * Una regola senza cadenza non entra: significa «si tiene l'ipotesi della
 * sezione», non «nessuna cadenza». La regola e' quella di {@link regolePerNodo}:
 * una seconda regola con la cadenza non prevale su una prima che non ce l'ha.
 */
export function cadenzeDichiarate(
  regole: readonly RegolaObbligo[],
): ReadonlyMap<IdNodo, Cadenza> {
  const dichiarate = new Map<IdNodo, Cadenza>();
  for (const [idNodo, regola] of regolePerNodo(regole)) {
    if (regola.cadenza_dichiarata) dichiarate.set(idNodo, regola.cadenza_dichiarata);
  }
  return dichiarate;
}

/**
 * Righe con un termine vero: aggiornamenti, fini di pubblicazione e scadenze
 * dei file.
 *
 * Gli obblighi mai valorizzati restano fuori: in «Scadenze e allerte» stanno
 * fra le allerte, nel gruppo «Obblighi senza contenuti», perche' non hanno un
 * termine da mostrare in una colonna «Scadenza» e mescolarli agli altri
 * rendeva illeggibile proprio la parte urgente.
 */
export function scadenzeConTermine(scadenze: readonly Scadenza[]): Scadenza[] {
  return scadenze.filter((scadenza) => scadenza.tipo !== "scoperto");
}

/**
 * Scadenze che cadono entro la finestra di preavviso, comprese quelle superate.
 *
 * Conta solo cio' che ha un termine: e' il numero del KPI «In scadenza», e deve
 * restare distinto da quello dei nodi scoperti, che ha un KPI suo.
 */
export function scadenzeImminenti(
  scadenze: readonly Scadenza[],
  giorni: number = GIORNI_PREAVVISO,
): Scadenza[] {
  return scadenzeConTermine(scadenze).filter((scadenza) => scadenza.giorni_residui <= giorni);
}
